import { GAMES_PLAYED_ABBRS, LOWER_IS_BETTER } from '../constants'
import type {
  ChartDomain,
  ChartPoint,
  ProgressBucket,
  ProgressChartModel,
  ProgressPeriod,
  StatValue,
} from '../types'
import { formatPeriodLabel } from './periodLabel'
import {
  getStatValue,
  isDerivedStat,
  isNoShotsSavePct,
  isRebuildableRate,
  rebuildRate,
  toStatNumber,
} from './statValues'

const SV_PCT = 'SV%'
const SV_PCT_PAD_BELOW = 0.01
const SV_PCT_PAD_ABOVE = 0.005
const DOMAIN_HEADROOM = 1.15

const statAbbr = (stat: StatValue): string | undefined =>
  stat.abbr ?? stat.type?.abbr

const sumStats = (periods: ProgressPeriod[]): Record<string, number> => {
  const sums: Record<string, number> = {}
  periods.forEach((period) => {
    period.stats?.forEach((stat) => {
      const abbr = statAbbr(stat)
      if (!abbr) {
        return
      }
      const value = toStatNumber(getStatValue([stat], abbr))
      if (value === null) {
        return
      }
      sums[abbr] = (sums[abbr] ?? 0) + value
    })
  })
  return sums
}

/** One period's value: count stats default to 0, rates are a gap without games. */
export const periodValue = (
  period: ProgressPeriod,
  abbr: string
): number | null => {
  const direct = toStatNumber(getStatValue(period.stats, abbr))
  if (!isDerivedStat(abbr)) {
    return direct ?? 0
  }
  if (period.games_played === 0) {
    return null
  }
  const sums = sumStats([period])
  // No shots faced: no save percentage, not a 0% period.
  if (isNoShotsSavePct(abbr, direct, sums.SV ?? null, sums.GA ?? null)) {
    return null
  }
  if (direct !== null) {
    return direct
  }
  return rebuildRate(abbr, sums, period.games_played, {
    missingNumeratorAsZero: true,
  })
}

const computeAggregate = (
  series: ProgressPeriod[],
  abbr: string,
  totalGames: number
): number | null => {
  if (isRebuildableRate(abbr)) {
    return rebuildRate(abbr, sumStats(series), totalGames, {
      missingNumeratorAsZero: true,
    })
  }
  if (isDerivedStat(abbr)) {
    return null
  }
  return sumStats(series)[abbr] ?? 0
}

const pickBest = (points: ChartPoint[], abbr: string): ChartPoint | null => {
  const lowerIsBetter = LOWER_IS_BETTER.includes(abbr)
  return points.reduce<ChartPoint | null>((best, point) => {
    if (point.gamesPlayed <= 0 || point.value === null) {
      return best
    }
    if (best === null || best.value === null) {
      return point
    }
    const better = lowerIsBetter
      ? point.value < best.value
      : point.value > best.value
    return better ? point : best
  }, null)
}

const computeDomain = (points: ChartPoint[], abbr: string): ChartDomain => {
  const values = points
    .map((p) => p.value)
    .filter((v): v is number => v !== null)
  const maxValue = values.length ? Math.max(...values) : 0
  if (abbr === SV_PCT && values.length) {
    return {
      min: Math.min(...values) - SV_PCT_PAD_BELOW,
      max: maxValue + SV_PCT_PAD_ABOVE,
    }
  }
  return { min: 0, max: maxValue > 0 ? maxValue * DOMAIN_HEADROOM : 1 }
}

export const listChartStats = (series: ProgressPeriod[]): string[] => {
  const seen: string[] = []
  series.forEach((period) => {
    period.stats?.forEach((stat) => {
      const abbr = statAbbr(stat)
      if (!abbr || GAMES_PLAYED_ABBRS.includes(abbr) || seen.includes(abbr)) {
        return
      }
      seen.push(abbr)
    })
  })
  return seen
}

export const buildProgressChart = (
  series: ProgressPeriod[],
  abbr: string,
  bucket: ProgressBucket
): ProgressChartModel => {
  const points: ChartPoint[] = series.map((period, index) => ({
    index,
    label: formatPeriodLabel(period.period_start, bucket),
    gamesPlayed: period.games_played,
    value: periodValue(period, abbr),
  }))
  const totalGames = series.reduce((sum, p) => sum + p.games_played, 0)
  return {
    points,
    domain: computeDomain(points, abbr),
    aggregate: computeAggregate(series, abbr, totalGames),
    best: pickBest(points, abbr),
    totalGames,
  }
}
